import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, Plus, Trash2, Save, Copy, CheckCircle2, Unlock, FileText, ChevronDown, ChevronUp, UserPlus,
  Download, Send,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const IN = "mt-1 w-full bg-white border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] px-3 py-2.5 text-sm";
const LB = "text-[10px] uppercase tracking-[0.2em] text-[#757067]";
const LEER = { name: "", telefon: "", email: "", stundenlohn_eur: 18, verdienstgrenze_eur: 556, notiz: "", aktiv: true };
const num = (v) => (v ?? 0).toFixed(2).replace(".", ",");
const monatJetzt = () => new Date().toISOString().slice(0, 7);

const STATUS_LABEL = { offen: "offen", eingereicht: "wartet auf Freigabe", freigegeben: "freigegeben" };

export default function ZeiterfassungTab({ pwd }) {
  const headers = { "X-Admin-Password": pwd };
  const [monat, setMonat] = useState(monatJetzt());
  const [rows, setRows] = useState([]);
  const [neu, setNeu] = useState(LEER);
  const [zeigeNeu, setZeigeNeu] = useState(false);
  const [busy, setBusy] = useState(false);
  const [offen, setOffen] = useState({});
  const [edit, setEdit] = useState(null);
  const [cfg, setCfg] = useState({ steuerbuero_email: "", steuerbuero_name: "", automatisch: false });

  const loadCfg = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/zeiterfassung-einstellungen`, { headers });
      setCfg({
        steuerbuero_email: data.steuerbuero_email || "",
        steuerbuero_name: data.steuerbuero_name || "",
        automatisch: !!data.automatisch,
        letzter_versand_monat: data.letzter_versand_monat || "",
      });
    } catch { /* leise */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pwd]);

  const cfgSpeichern = async () => {
    try {
      await axios.put(`${API}/admin/zeiterfassung-einstellungen`, {
        steuerbuero_email: cfg.steuerbuero_email, steuerbuero_name: cfg.steuerbuero_name,
        automatisch: cfg.automatisch,
      }, { headers });
      toast.success("Einstellungen gespeichert");
    } catch { toast.error("Speichern fehlgeschlagen"); }
  };

  const csvLaden = async () => {
    try {
      const res = await axios.get(`${API}/admin/zeiterfassung-export`, {
        params: { monat }, headers, responseType: "blob",
      });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data);
      a.download = `Stunden-${monat}.csv`;
      a.click();
      toast.success("CSV heruntergeladen");
    } catch { toast.error("CSV konnte nicht erstellt werden"); }
  };

  const anSteuerbuero = async () => {
    if (!cfg.steuerbuero_email) { toast.error("Bitte zuerst die E-Mail des Steuerbüros speichern"); return; }
    if (!window.confirm(`Stunden für ${monat} jetzt an ${cfg.steuerbuero_email} senden?`)) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/zeiterfassung-export/senden`, null, {
        params: { monat }, headers,
      });
      toast.success(`Gesendet: ${data.mitarbeiter} Mitarbeiter, ${data.summe_stunden} Std.`);
      await loadCfg();
    } catch (err) { toast.error(err.response?.data?.detail || "Versand fehlgeschlagen"); }
    finally { setBusy(false); }
  };

  const load = useCallback(async (m) => {
    try {
      const { data } = await axios.get(`${API}/admin/zeiterfassung`, { params: { monat: m }, headers });
      setRows(data.mitarbeiter || []);
    } catch {
      toast.error("Zeiterfassung konnte nicht geladen werden");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pwd]);

  useEffect(() => { load(monat); }, [load, monat]);
  useEffect(() => { loadCfg(); }, [loadCfg]);

  const anlegen = async () => {
    if (!neu.name.trim()) { toast.error("Bitte einen Namen eingeben"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/admin/mitarbeiter`, {
        ...neu,
        stundenlohn_eur: Number(neu.stundenlohn_eur) || 0,
        verdienstgrenze_eur: Number(neu.verdienstgrenze_eur) || 0,
      }, { headers });
      setNeu(LEER);
      setZeigeNeu(false);
      await load(monat);
      toast.success("Mitarbeiter angelegt");
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler beim Anlegen"); }
    finally { setBusy(false); }
  };

  const speichern = async (r) => {
    try {
      await axios.put(`${API}/admin/mitarbeiter/${r.id}`, {
        name: r.name, telefon: r.telefon, email: r.email,
        stundenlohn_eur: Number(r.stundenlohn_eur) || 0,
        verdienstgrenze_eur: Number(r.verdienstgrenze_eur) || 0,
        notiz: r.notiz || "", aktiv: r.aktiv !== false,
      }, { headers });
      setEdit(null);
      await load(monat);
      toast.success("Gespeichert");
    } catch (err) { toast.error(err.response?.data?.detail || "Speichern fehlgeschlagen"); }
  };

  const loeschen = async (r) => {
    if (!window.confirm(`${r.name} samt allen erfassten Zeiten löschen?`)) return;
    await axios.delete(`${API}/admin/mitarbeiter/${r.id}`, { headers });
    await load(monat);
  };

  const freigeben = async (r) => {
    try {
      await axios.post(`${API}/admin/zeiterfassung/${r.id}/${monat}/freigeben`, {}, { headers });
      await load(monat);
      toast.success(`${r.name}: Monat freigegeben`);
    } catch (err) { toast.error(err.response?.data?.detail || "Freigabe fehlgeschlagen"); }
  };

  const wiederOeffnen = async (r) => {
    await axios.post(`${API}/admin/zeiterfassung/${r.id}/${monat}/oeffnen`, {}, { headers });
    await load(monat);
    toast.success(`${r.name}: Monat wieder geöffnet`);
  };

  const zeitLoeschen = async (id) => {
    await axios.delete(`${API}/admin/zeiterfassung/zeiten/${id}`, { headers });
    await load(monat);
  };

  const tagPdf = async (z) => {
    try {
      const res = await axios.get(`${API}/admin/zeiterfassung-tag/${z.id}/pdf`, {
        headers, responseType: "blob",
      });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch { toast.error("Stundenzettel konnte nicht erstellt werden"); }
  };

  const tagFreigeben = async (z) => {
    try {
      await axios.post(`${API}/admin/zeiterfassung-tag/${z.id}/freigeben`, {}, { headers });
      await load(monat);
      toast.success("Tag freigegeben");
    } catch (err) { toast.error(err.response?.data?.detail || "Freigabe fehlgeschlagen"); }
  };

  const pdf = async (r) => {    try {
      const res = await axios.get(`${API}/admin/zeiterfassung/${r.id}/${monat}/pdf`, {
        headers, responseType: "blob",
      });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch { toast.error("PDF konnte nicht erstellt werden"); }
  };

  const linkKopieren = async (r) => {
    await navigator.clipboard.writeText(r.link);
    toast.success("Link kopiert — an den Mitarbeiter schicken");
  };

  return (
    <div data-testid="zeiterfassung-tab" className="space-y-8">
      <div>
        <p className="text-xs uppercase tracking-[0.25em] text-[#BA6D52] font-semibold">Zeiterfassung</p>
        <p className="text-sm text-[#757067] mt-1 max-w-2xl">
          Jeder Mitarbeiter bekommt einen persönlichen Link und trägt dort seine Arbeitszeiten ein.
          Am Monatsende schickt er sie mit digitaler Unterschrift ab — Du gibst frei und bekommst einen
          Stundennachweis als PDF (Beginn, Ende, Dauer, Pausen — wie vom Gesetz gefordert).
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className={LB}>Monat</label>
          <input type="month" value={monat} onChange={(e) => setMonat(e.target.value)}
            data-testid="ze-monat" className={IN} />
        </div>
        <button onClick={() => setZeigeNeu((v) => !v)} data-testid="ze-neu-toggle"
          className="border border-[#BA6D52] text-[#BA6D52] hover:bg-[#FAF5F1] px-5 py-2.5 text-xs uppercase tracking-[0.15em] inline-flex items-center gap-2">
          <UserPlus size={14} /> Mitarbeiter anlegen
        </button>
        <button onClick={csvLaden} data-testid="ze-csv"
          className="border border-[#E3DECE] text-[#757067] hover:bg-[#FAFAF7] px-5 py-2.5 text-xs uppercase tracking-[0.15em] inline-flex items-center gap-2">
          <Download size={14} /> CSV für Lohn
        </button>
        <button onClick={anSteuerbuero} disabled={busy} data-testid="ze-senden"
          className="bg-[#2C2A26] text-white hover:bg-[#1F1D1A] px-5 py-2.5 text-xs uppercase tracking-[0.15em] inline-flex items-center gap-2">
          <Send size={14} /> An Steuerbüro senden
        </button>
      </div>

      <div className="border border-[#E3DECE] bg-[#FAFAF7] p-5" data-testid="ze-steuerbuero-box">
        <p className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52] mb-1">Steuerbüro</p>
        <p className="text-xs text-[#757067] mb-4">
          CSV (alle Tage, Stunden und Beträge) plus die einzelnen Stundennachweise als PDF gehen per Mail raus.
          Mit dem Haken schickt das Portal das automatisch am 1. jedes Monats für den Vormonat.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className={LB}>E-Mail Steuerbüro</label>
            <input value={cfg.steuerbuero_email} data-testid="ze-stb-email" placeholder="buero@…"
              onChange={(e) => setCfg((c) => ({ ...c, steuerbuero_email: e.target.value }))} className={IN} />
          </div>
          <div>
            <label className={LB}>Ansprechpartner (optional)</label>
            <input value={cfg.steuerbuero_name} data-testid="ze-stb-name"
              onChange={(e) => setCfg((c) => ({ ...c, steuerbuero_name: e.target.value }))} className={IN} />
          </div>
          <div className="flex flex-col justify-end gap-3">
            <label className="flex items-center gap-2 text-sm text-[#2C2A26]">
              <input type="checkbox" checked={!!cfg.automatisch} data-testid="ze-stb-auto"
                onChange={(e) => setCfg((c) => ({ ...c, automatisch: e.target.checked }))} />
              Jeden Monat automatisch senden
            </label>
            <button onClick={cfgSpeichern} data-testid="ze-stb-save"
              className="bg-[#BA6D52] text-white hover:bg-[#A55C43] px-5 py-2.5 text-xs uppercase tracking-[0.15em] inline-flex items-center gap-2 self-start">
              <Save size={14} /> Speichern
            </button>
          </div>
        </div>
        {cfg.letzter_versand_monat && (
          <p className="text-xs text-[#757067] mt-3" data-testid="ze-stb-letzter">
            Zuletzt gesendet: {cfg.letzter_versand_monat}
          </p>
        )}
      </div>

      {zeigeNeu && (
        <div className="border border-[#E3DECE] bg-[#FAFAF7] p-5" data-testid="ze-neu-box">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className={LB}>Name</label>
              <input value={neu.name} onChange={(e) => setNeu((n) => ({ ...n, name: e.target.value }))}
                data-testid="ze-neu-name" className={IN} />
            </div>
            <div>
              <label className={LB}>Handy (für WhatsApp)</label>
              <input value={neu.telefon} onChange={(e) => setNeu((n) => ({ ...n, telefon: e.target.value }))}
                data-testid="ze-neu-telefon" placeholder="0151 …" className={IN} />
            </div>
            <div>
              <label className={LB}>E-Mail (optional)</label>
              <input value={neu.email} onChange={(e) => setNeu((n) => ({ ...n, email: e.target.value }))}
                data-testid="ze-neu-email" className={IN} />
            </div>
            <div>
              <label className={LB}>Stundenlohn brutto (€)</label>
              <input type="number" step="0.5" value={neu.stundenlohn_eur}
                onChange={(e) => setNeu((n) => ({ ...n, stundenlohn_eur: e.target.value }))}
                data-testid="ze-neu-lohn" className={IN} />
            </div>
            <div>
              <label className={LB}>Verdienstgrenze (€/Monat)</label>
              <input type="number" step="1" value={neu.verdienstgrenze_eur}
                onChange={(e) => setNeu((n) => ({ ...n, verdienstgrenze_eur: e.target.value }))}
                data-testid="ze-neu-grenze" className={IN} />
            </div>
            <div>
              <label className={LB}>Notiz</label>
              <input value={neu.notiz} onChange={(e) => setNeu((n) => ({ ...n, notiz: e.target.value }))}
                data-testid="ze-neu-notiz" className={IN} />
            </div>
          </div>
          <div className="mt-5 flex justify-end">
            <button onClick={anlegen} disabled={busy} data-testid="ze-neu-speichern"
              className="bg-[#BA6D52] text-white hover:bg-[#A55C43] px-5 py-2.5 text-xs uppercase tracking-[0.15em] inline-flex items-center gap-2">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Anlegen
            </button>
          </div>
        </div>
      )}

      {rows.length === 0 ? (
        <p className="text-sm text-[#757067]" data-testid="ze-leer">
          Noch kein Mitarbeiter angelegt. Leg oben den ersten an.
        </p>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => {
            const auf = !!offen[r.id];
            const bearbeiten = edit?.id === r.id ? edit : null;
            return (
              <div key={r.id} className="border border-[#E3DECE] bg-white" data-testid={`ze-row-${r.id}`}>
                <div className="px-4 py-4 flex flex-wrap items-start justify-between gap-4">
                  <div className="min-w-[220px]">
                    <p className="font-serif text-lg text-[#2C2A26]">{r.name}</p>
                    <p className="text-xs text-[#757067] mt-1">
                      {num(r.stundenlohn_eur)} €/Std. · Grenze {num(r.grenze_eur)} €
                      {r.telefon ? ` · ${r.telefon}` : ""}
                    </p>
                    <p className="text-[11px] uppercase tracking-[0.15em] mt-2 text-[#BA6D52]">
                      {STATUS_LABEL[r.status] || r.status}
                      {r.eingereicht_am && r.status === "eingereicht" ? " · abgeschickt" : ""}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-serif text-2xl text-[#2C2A26]" data-testid={`ze-stunden-${r.id}`}>
                      {num(r.summe_stunden)} Std.
                    </p>
                    <p className={`text-sm mt-1 ${r.ueber_grenze ? "text-[#B03A2E]" : "text-[#757067]"}`}
                      data-testid={`ze-verdienst-${r.id}`}>
                      {num(r.verdienst_eur)} € brutto{r.ueber_grenze ? " · über Grenze!" : ""}
                    </p>
                  </div>
                </div>

                <div className="px-4 pb-4 flex flex-wrap gap-2">
                  <button onClick={() => setOffen((o) => ({ ...o, [r.id]: !auf }))} data-testid={`ze-toggle-${r.id}`}
                    className="text-[10px] uppercase tracking-[0.15em] border border-[#E3DECE] text-[#757067] hover:bg-[#FAFAF7] px-3 py-2 inline-flex items-center gap-2">
                    {auf ? <ChevronUp size={13} /> : <ChevronDown size={13} />} Zeiten ({(r.eintraege || []).length})
                  </button>
                  {r.status !== "freigegeben" ? (
                    <button onClick={() => freigeben(r)} data-testid={`ze-freigeben-${r.id}`}
                      className="text-[10px] uppercase tracking-[0.15em] bg-[#BA6D52] text-white hover:bg-[#A55C43] px-3 py-2 inline-flex items-center gap-2">
                      <CheckCircle2 size={13} /> Monat freigeben
                    </button>
                  ) : (
                    <button onClick={() => wiederOeffnen(r)} data-testid={`ze-oeffnen-${r.id}`}
                      className="text-[10px] uppercase tracking-[0.15em] border border-[#E3DECE] text-[#757067] hover:bg-[#FAFAF7] px-3 py-2 inline-flex items-center gap-2">
                      <Unlock size={13} /> Wieder öffnen
                    </button>
                  )}
                  <button onClick={() => pdf(r)} data-testid={`ze-pdf-${r.id}`}
                    className="text-[10px] uppercase tracking-[0.15em] border border-[#E3DECE] text-[#757067] hover:bg-[#FAFAF7] px-3 py-2 inline-flex items-center gap-2">
                    <FileText size={13} /> PDF
                  </button>
                  <button onClick={() => linkKopieren(r)} data-testid={`ze-link-${r.id}`}
                    className="text-[10px] uppercase tracking-[0.15em] border border-[#E3DECE] text-[#757067] hover:bg-[#FAFAF7] px-3 py-2 inline-flex items-center gap-2">
                    <Copy size={13} /> Link kopieren
                  </button>
                  <a href={r.whatsapp_url} target="_blank" rel="noreferrer" data-testid={`ze-wa-${r.id}`}
                    className="text-[10px] uppercase tracking-[0.15em] border border-[#E3DECE] text-[#757067] hover:bg-[#FAFAF7] px-3 py-2 inline-flex items-center gap-2">
                    WhatsApp
                  </a>
                  <button onClick={() => setEdit(bearbeiten ? null : { ...r })} data-testid={`ze-edit-${r.id}`}
                    className="text-[10px] uppercase tracking-[0.15em] border border-[#E3DECE] text-[#757067] hover:bg-[#FAFAF7] px-3 py-2">
                    Bearbeiten
                  </button>
                  <button onClick={() => loeschen(r)} data-testid={`ze-del-${r.id}`}
                    className="border border-[#E3DECE] text-[#757067] hover:bg-red-50 hover:text-red-700 px-3 py-2">
                    <Trash2 size={13} />
                  </button>
                </div>

                {bearbeiten && (
                  <div className="px-4 pb-5 border-t border-[#E3DECE] pt-4 bg-[#FAFAF7]">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className={LB}>Name</label>
                        <input value={bearbeiten.name} data-testid={`ze-edit-name-${r.id}`}
                          onChange={(e) => setEdit({ ...bearbeiten, name: e.target.value })} className={IN} />
                      </div>
                      <div>
                        <label className={LB}>Handy</label>
                        <input value={bearbeiten.telefon || ""} data-testid={`ze-edit-telefon-${r.id}`}
                          onChange={(e) => setEdit({ ...bearbeiten, telefon: e.target.value })} className={IN} />
                      </div>
                      <div>
                        <label className={LB}>E-Mail</label>
                        <input value={bearbeiten.email || ""} data-testid={`ze-edit-email-${r.id}`}
                          onChange={(e) => setEdit({ ...bearbeiten, email: e.target.value })} className={IN} />
                      </div>
                      <div>
                        <label className={LB}>Stundenlohn (€)</label>
                        <input type="number" step="0.5" value={bearbeiten.stundenlohn_eur}
                          data-testid={`ze-edit-lohn-${r.id}`}
                          onChange={(e) => setEdit({ ...bearbeiten, stundenlohn_eur: e.target.value })} className={IN} />
                      </div>
                      <div>
                        <label className={LB}>Verdienstgrenze (€)</label>
                        <input type="number" step="1" value={bearbeiten.verdienstgrenze_eur ?? bearbeiten.grenze_eur}
                          data-testid={`ze-edit-grenze-${r.id}`}
                          onChange={(e) => setEdit({ ...bearbeiten, verdienstgrenze_eur: e.target.value })} className={IN} />
                      </div>
                      <div>
                        <label className={LB}>Notiz</label>
                        <input value={bearbeiten.notiz || ""} data-testid={`ze-edit-notiz-${r.id}`}
                          onChange={(e) => setEdit({ ...bearbeiten, notiz: e.target.value })} className={IN} />
                      </div>
                    </div>
                    <div className="mt-4 flex justify-end">
                      <button onClick={() => speichern(bearbeiten)} data-testid={`ze-edit-save-${r.id}`}
                        className="bg-[#BA6D52] text-white hover:bg-[#A55C43] px-5 py-2.5 text-xs uppercase tracking-[0.15em] inline-flex items-center gap-2">
                        <Save size={14} /> Speichern
                      </button>
                    </div>
                  </div>
                )}

                {auf && (
                  <div className="px-4 pb-5 border-t border-[#E3DECE] pt-4" data-testid={`ze-zeiten-${r.id}`}>
                    {(r.eintraege || []).length === 0 ? (
                      <p className="text-sm text-[#757067]">Noch keine Zeiten in diesem Monat.</p>
                    ) : (
                      <div className="space-y-1.5">
                        {r.eintraege.map((z) => (
                          <div key={z.id} className="flex items-center justify-between gap-3 text-sm py-1.5 border-b border-[#F0EBE0]">
                            <span className="text-[#2C2A26]">
                              {z.datum.split("-").reverse().join(".")} · {z.von}–{z.bis}
                              {z.pause_min ? ` · ${z.pause_min} Min. Pause` : ""}
                              {z.notiz ? ` · ${z.notiz}` : ""}
                              {z.bestaetigt_am ? " · bestätigt" : ""}
                            </span>
                            <span className="flex items-center gap-2">
                              <span className="font-medium">{num(z.stunden)} Std.</span>
                              <button onClick={() => tagPdf(z)} data-testid={`ze-tag-pdf-${z.id}`}
                                title="Tages-Stundenzettel als PDF"
                                className="text-[10px] uppercase tracking-[0.15em] border border-[#E3DECE] text-[#757067] hover:bg-[#FAFAF7] px-2 py-1.5">
                                PDF
                              </button>
                              {z.status !== "freigegeben" && (
                                <button onClick={() => tagFreigeben(z)} data-testid={`ze-tag-freigeben-${z.id}`}
                                  className="text-[10px] uppercase tracking-[0.15em] border border-[#BA6D52] text-[#BA6D52] hover:bg-[#FAF5F1] px-2 py-1.5">
                                  Tag freigeben
                                </button>
                              )}
                              {r.status !== "freigegeben" && (
                                <button onClick={() => zeitLoeschen(z.id)} data-testid={`ze-zeit-del-${z.id}`}
                                  className="text-[#757067] hover:text-red-700">
                                  <Trash2 size={13} />
                                </button>
                              )}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
